import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Search, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useState, useEffect } from 'react';
import api from '@/utils/api';

const LandingPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    hero_title: 'Booking Lapangan',
    hero_subtitle: 'Zero Futsal',
    primary_color: '#CEFF05',
    logo_url: ''
  });
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusBookingId, setStatusBookingId] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusResult, setStatusResult] = useState(null);
  const [showVenueModal, setShowVenueModal] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadSettings();
    fetchVenues();
  }, []);

  const fetchVenues = async () => {
    try {
      const response = await api.get('/venues');
      setVenues(response.data);
    } catch (error) {
      console.error('Failed to fetch venues:', error);
      toast.error('Gagal memuat daftar lapangan');
    }
  };

  const loadSettings = async () => {
    try {
      const response = await api.get('/settings/public');
      if (response.data && Object.keys(response.data).length > 0) {
        setSettings({
            hero_title: response.data.hero_title || 'Booking Lapangan',
            hero_subtitle: response.data.hero_subtitle || 'Zero Futsal',
            primary_color: response.data.primary_color || '#CEFF05',
            logo_url: response.data.logo_url || ''
        });
        
        // Apply primary color to root for global usage if needed, 
        // or just use it in this component
        document.documentElement.style.setProperty('--primary', response.data.primary_color || '#CEFF05');
      }
    } catch (error) {
      console.error('Failed to load settings', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const s = String(status || '').toLowerCase();
    const map = {
      pending: { text: 'Pending', className: 'bg-secondary/15 text-secondary border-secondary/30 shadow-[0_0_18px_rgba(250,204,21,0.10)]' },
      confirmed: { text: 'Confirmed', className: 'bg-primary/15 text-primary border-primary/30 shadow-[0_0_18px_rgba(206,255,5,0.12)]' },
      verified: { text: 'Verified', className: 'bg-primary/15 text-primary border-primary/30 shadow-[0_0_18px_rgba(206,255,5,0.12)]' },
      rejected: { text: 'Rejected', className: 'bg-red-500/15 text-red-300 border-red-500/30 shadow-[0_0_18px_rgba(239,68,68,0.10)]' },
      cancelled: { text: 'Cancelled', className: 'bg-white/5 text-white/60 border-white/10' },
    };
    const badge = map[s] || { text: String(status || '-'), className: 'bg-white/5 text-white/60 border-white/10' };
    return <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase border ${badge.className}`}>{badge.text}</span>;
  };

  const handleCheckStatus = async () => {
    const id = String(statusBookingId || '').trim();
    if (!id) {
      toast.error('Masukkan Booking ID');
      return;
    }
    setStatusLoading(true);
    try {
      const response = await api.get(`/bookings/status/${id}`);
      setStatusResult(response.data);
    } catch (e) {
      setStatusResult(null);
      toast.error(e.response?.data?.detail || 'Booking tidak ditemukan');
    } finally {
      setStatusLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="border-b border-white/10 bg-background-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-3">
              {settings.logo_url ? (
                <img src={settings.logo_url} alt="Logo" className="h-10 w-10 object-contain rounded-md" />
              ) : (
                <img src="/zero-logo.png" alt="Logo" className="h-10 w-10 object-contain rounded-md" />
              )}
            </Link>
            
            <div className="flex items-center gap-4">
              {token ? (
                <>
                  <Link 
                    to="/my-bookings"
                    data-testid="my-bookings-link"
                    className="text-white/60 hover:text-primary font-medium transition-colors"
                  >
                    Booking Saya
                  </Link>
                  <button
                    data-testid="logout-btn"
                    onClick={() => {
                      localStorage.removeItem('token');
                      localStorage.removeItem('user');
                      toast.success('Logout berhasil');
                      navigate('/');
                    }}
                    className="text-white/60 hover:text-red-400 font-medium transition-colors"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link 
                    to="/login"
                    data-testid="login-link"
                    className="text-white/60 hover:text-primary font-medium transition-colors"
                  >
                    Masuk
                  </Link>
                  <Link 
                    to="/register"
                    data-testid="register-link"
                    className="bg-primary text-black px-8 py-3 rounded-full font-bold uppercase tracking-wider hover:bg-primary-hover transition-all shadow-[0_0_0_1px_rgba(206,255,5,0.35),0_10px_30px_rgba(0,0,0,0.45)]"
                  >
                    Daftar
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.86), rgba(0,0,0,0.86)), url('https://images.unsplash.com/photo-1769770639042-05e6b54a70cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxuZW9uJTIwZnV0c2FsJTIwZmllbGQlMjBpbmRvb3IlMjBuaWdodHxlbnwwfHx8fDE3NzA4MDAzNzR8MA&ixlib=rb-4.1.0&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-5xl mx-auto"
          >
            <h1 className="text-6xl md:text-8xl font-heading font-black uppercase tracking-tighter mb-6 leading-[0.95]">
              <span className="block text-white">BOOK</span>
              <span className="block text-primary glow-text -mt-2">FUTSAL</span>
              <span className="block text-white/70 text-3xl md:text-4xl font-body font-semibold tracking-tight mt-4">
                {settings.hero_title} — {settings.hero_subtitle}
              </span>
            </h1>
            <p className="text-lg md:text-xl text-white/60 font-body mb-10 max-w-2xl mx-auto">
              Pesan lapangan futsal favorit Anda dengan mudah. Cepat, aman, dan terpercaya.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => setShowVenueModal(true)}
                className="bg-primary text-black px-10 py-4 rounded-full font-bold uppercase tracking-wider hover:bg-primary-hover transition-all shadow-[0_0_0_1px_rgba(206,255,5,0.45),0_0_36px_rgba(206,255,5,0.16)]"
              >
                Booking Sekarang
              </button>
              <Link
                to="/my-bookings"
                className="border border-white/10 text-white px-10 py-4 rounded-full font-bold uppercase tracking-wider hover:bg-white/5 hover:border-primary/40 transition-all"
              >
                Lihat Booking
              </Link>
            </div>

            <div className="mt-12 max-w-3xl mx-auto glass glow-border rounded-3xl p-6 text-left">
              <div className="text-sm font-bold uppercase tracking-wider text-white mb-3">Cek Status Booking</div>
              <div className="flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-white/40" />
                  </div>
                  <input
                    value={statusBookingId}
                    onChange={(e) => setStatusBookingId(e.target.value)}
                    placeholder="Masukkan Booking ID (contoh: 6ca95df8-...)"
                    className="w-full bg-black/30 border border-white/10 rounded-2xl pl-10 pr-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleCheckStatus}
                  disabled={statusLoading}
                  className="bg-primary text-black px-10 py-3 rounded-full font-bold uppercase tracking-wider hover:bg-primary-hover transition-all disabled:opacity-50"
                >
                  {statusLoading ? 'Mencari...' : 'Cek Status'}
                </button>
              </div>

              {statusResult && (
                <div className="mt-5 border-t border-white/10 pt-5">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <div className="text-xs text-white/50 uppercase tracking-wider">Booking ID</div>
                      <div className="text-white font-mono">{statusResult.booking_id}</div>
                    </div>
                    <div className="flex gap-2">
                      {getStatusBadge(statusResult.booking_status)}
                      {statusResult.payment_status ? getStatusBadge(statusResult.payment_status) : getStatusBadge('pending')}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                    <div>
                      <div className="text-xs text-white/50 uppercase tracking-wider">Lapangan</div>
                      <div className="text-white font-bold">{statusResult.venue_name || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-white/50 uppercase tracking-wider">Tanggal</div>
                      <div className="text-white font-bold">{statusResult.booking_date || '-'}</div>
                    </div>
                    <div className="sm:col-span-2">
                      <div className="text-xs text-white/50 uppercase tracking-wider">Jam</div>
                      <div className="text-white font-bold">
                        {Array.isArray(statusResult.time_slots) ? statusResult.time_slots.join(', ') : '-'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Feature Cards */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16"
          >
            {[
              { icon: Calendar, title: 'Booking Real-time', desc: 'Lihat slot tersedia secara langsung' },
              { icon: MapPin, title: 'Lokasi Strategis', desc: 'Lapangan mudah diakses' },
              { icon: Users, title: 'Verified Players', desc: 'Komunitas pemain terpercaya' }
            ].map((feature, idx) => (
              <div key={idx} className="glass glow-border rounded-3xl p-6 relative overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <feature.icon className="w-10 h-10 text-primary mb-4" />
                <h3 className="text-xl font-bold text-white mb-2 uppercase font-heading">{feature.title}</h3>
                <p className="text-white/60">{feature.desc}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Venues Section */}
      <section className="py-24 bg-background-paper/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-heading font-black uppercase mb-4 text-white">
              Pilih <span className="text-primary glow-text">Lapangan</span>
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Tersedia berbagai jenis lapangan dengan kualitas terbaik untuk permainan maksimal Anda.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {venues.map((venue, index) => (
              <motion.div
                key={venue.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group glass glow-border rounded-3xl overflow-hidden transition-all duration-300"
              >
                <div className="aspect-video relative overflow-hidden bg-zinc-800">
                  {venue.image_url ? (
                    <img 
                      src={venue.image_url} 
                      alt={venue.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-zinc-800">
                       <MapPin className="w-12 h-12 text-zinc-600" />
                    </div>
                  )}
                  <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-md px-3 py-1 rounded-full border border-white/10">
                    <span className="text-xs font-bold text-white/80 uppercase tracking-wider">{venue.type}</span>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-2xl font-heading font-black uppercase text-white mb-2">{venue.name}</h3>
                  <p className="text-white/60 text-sm line-clamp-2 mb-6">{venue.description}</p>
                  
                  <div className="flex items-center justify-between mt-auto">
                    <div>
                      <p className="text-xs text-white/50 uppercase tracking-wider mb-1">Harga / Jam</p>
                      {Number(venue.price_per_hour) > 0 ? (
                        <p className="text-xl font-black text-primary glow-text">Rp {venue.price_per_hour.toLocaleString()}</p>
                      ) : (
                        <p className="text-sm font-bold text-white/60 uppercase tracking-wider">Mengikuti aturan</p>
                      )}
                    </div>
                    
                    <Link 
                      to={`/booking/${venue.id}`}
                      className="bg-primary text-black px-8 py-3 rounded-full font-bold uppercase tracking-wider text-sm hover:bg-primary-hover transition-all"
                    >
                      Book Now
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          {venues.length === 0 && !loading && (
             <div className="text-center py-12">
                <p className="text-zinc-500">Belum ada lapangan yang tersedia.</p>
             </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 bg-background-paper py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-zinc-500 font-body">
              © 2026 Zero Futsal. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
      {/* Venue Selection Modal */}
      {showVenueModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-zinc-900 border border-white/10 p-6 rounded-3xl w-full max-w-md shadow-2xl"
          >
            <h3 className="text-2xl font-heading font-black uppercase text-white mb-6 text-center">Pilih Lapangan</h3>
            <div className="flex flex-col gap-3">
              {venues.map((v) => (
                <Link
                  key={v.id}
                  to={`/booking/${v.id}`}
                  className="bg-black/50 border border-white/10 hover:border-primary/50 text-white p-4 rounded-xl flex items-center justify-between transition-all group"
                >
                  <span className="font-bold uppercase tracking-wider group-hover:text-primary transition-colors">{v.name}</span>
                  <span className="text-primary opacity-0 group-hover:opacity-100 transition-opacity">&rarr;</span>
                </Link>
              ))}
              {venues.length === 0 && (
                <p className="text-white/50 text-center text-sm py-4">Belum ada lapangan.</p>
              )}
            </div>
            <button 
              onClick={() => setShowVenueModal(false)}
              className="w-full mt-6 text-white/50 hover:text-white uppercase tracking-wider text-sm font-bold transition-colors"
            >
              Batal
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
